<template>
  <v-autocomplete
    :model-value="modelValue"
    :items="items"
    :item-title="itemTitle"
    :item-value="itemValue"
    :label="label"
    :density="density"
    :clearable="clearable"
    :readonly="readonly || saving"
    :disabled="disabled"
    :multiple="multiple"
    :return-object="returnObject"
    :hide-details="hideDetails"
    v-bind="$attrs"
    @update:model-value="onSelect"
  >
    <template #append-inner>
      <v-progress-circular
        v-if="saving"
        indeterminate
        size="18"
        width="2"
        color="primary"
      />
      <v-icon v-else-if="saveStatus === 'success'" color="success" size="18">
        mdi-check-circle-outline
      </v-icon>
      <v-icon v-else-if="saveStatus === 'error'" color="error" size="18">
        mdi-alert-circle-outline
      </v-icon>
    </template>

    <!-- Forward all named slots from parent -->
    <template v-for="(_, name) in $slots" #[name]="slotProps">
      <slot :name="name" v-bind="slotProps ?? {}" />
    </template>
  </v-autocomplete>
</template>

<script setup lang="ts">
import { ref } from "vue";

const props = withDefaults(
  defineProps<{
    modelValue: unknown;
    items: unknown[];
    onSave: (value: unknown) => Promise<unknown>;
    itemTitle?: string;
    itemValue?: string;
    label?: string;
    density?: "compact" | "comfortable" | "default";
    clearable?: boolean;
    readonly?: boolean;
    disabled?: boolean;
    multiple?: boolean;
    returnObject?: boolean;
    hideDetails?: boolean | "auto";
  }>(),
  {
    density: "compact",
    hideDetails: "auto",
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", value: unknown): void;
  (e: "saved", value: unknown): void;
  (e: "error", err: unknown): void;
}>();

const saving = ref(false);
const saveStatus = ref<"idle" | "success" | "error">("idle");

let statusResetTimer: ReturnType<typeof setTimeout> | null = null;

async function onSelect(value: unknown) {
  emit("update:modelValue", value);

  if (statusResetTimer) {
    clearTimeout(statusResetTimer);
    statusResetTimer = null;
  }

  saving.value = true;
  saveStatus.value = "idle";

  try {
    await props.onSave(value);
    saveStatus.value = "success";
    emit("saved", value);
  } catch (err) {
    saveStatus.value = "error";
    emit("error", err);
  } finally {
    saving.value = false;
    statusResetTimer = setTimeout(() => {
      saveStatus.value = "idle";
    }, 3000);
  }
}
</script>
